import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import IconButton from "@cayuse/shared-react.components.icon-button";
import {
	isNonEmptyArray,
	isNonEmptyString,
	isObjectWithKey,
} from "@cayuse/shared-react.utils.type-checks";
import { api } from "services";
import { v4 as uuidv4 } from "uuid";

import RuleConditionSelectorModal from "components/RuleConditionSelectorModal";
import { CAYUSE_TYPES, SELECT_ANY_KEY } from "globals/constants";
import { useDictionary, useResearchTeamRoles } from "hooks";
import { formSelectors } from "store/selectors";
import {
	findOrganizationById,
	findPersonById,
	findPicklistOptionByFieldIdAndOptionId,
	findUnitById,
	getResearchTeamFormElement,
	isFieldPicklist,
} from "utils";

/**
 * These "local hooks" are not useful outside of the builders, any
 * reusable hooks should be put in src/hooks
 */
export const useResearchTeamFormElement = formElements => {
	return useMemo(() => {
		const researchTeamFormElement =
            getResearchTeamFormElement(formElements);

		return isObjectWithKey(researchTeamFormElement, "id")
			? researchTeamFormElement.id
			: undefined;
	}, [ formElements ]);
};

export const useResearchTeamRoleLabel = (
	formElementId,
	formElements,
	roleId
) => {
	const [ roleLabel, setRoleLabel ] = useState("");

	useEffect(() => {
		const doIt = () => {
			return lookupSingleEntryLabel(formElements, formElementId, roleId);
		};

		doIt().then(label => setRoleLabel(label));
	}, [ formElementId, formElements, roleId ]);

	return roleLabel;
};

export const useUnusedResearchTeamRoles = (
	rules,
	currentRuleRoleId,
	targetRuleType
) => {
	const { roles } = useResearchTeamRoles();
	const initialRoles = isNonEmptyArray(roles) ? roles : [];
	const [ unusedRoles, setUnusedRoles ] = useState([ ...initialRoles ]);

	useEffect(() => {
		const usedRoleIds = [];

		if (isNonEmptyArray(rules)) {
			rules.forEach(({ ruleType, optionId }) => {
				const isCertRule = ruleType === targetRuleType;

				if (isCertRule && isNonEmptyString(optionId)) {
					usedRoleIds.push(optionId);
				}
			});
		}

		const updatedUnusedRoles = roles?.filter(({ id }) => {
			const isRoleUsed = usedRoleIds.includes(id);

			return currentRuleRoleId === id || !isRoleUsed;
		});

		setUnusedRoles(updatedUnusedRoles);
	}, [ rules, roles, currentRuleRoleId, targetRuleType ]);

	return unusedRoles;
};

export const useTooltip = ({ isTruncated, fullLabel, tooltipValueRef }) => {
	useEffect(() => {
		const tipValue = isTruncated ? fullLabel : null;

		if (tipValue !== tooltipValueRef) {
			tooltipValueRef.current = tipValue;
		}
	}, [ isTruncated, fullLabel, tooltipValueRef ]);
};

export const useRuleConditionSelector = ({
	open,
	rule,
	onClose,
	onRuleChange,
	onCancel,
	onUpdate,
	unitsOnly,
}) => {
	const { formElements, formSections } = useSelector(formSelectors.content);
	const dictionary = useDictionary("ruleBuilder");

	return useMemo(() => {
		return (
			<RuleConditionSelectorModal
				formElements={formElements}
				formSections={formSections}
				dictionary={dictionary}
				rule={rule}
				open={open}
				onClose={onClose}
				onCancel={onCancel}
				onRuleChange={onRuleChange}
				onUpdate={onUpdate}
				unitsOnly={unitsOnly}
			/>
		);
	}, [
		formElements,
		formSections,
		dictionary,
		rule,
		open,
		onClose,
		onCancel,
		onRuleChange,
		onUpdate,
		unitsOnly,
	]);
};

export const useAddConditionButton = ({ onClick, disabled }) => {
	const dictionary = useDictionary("ruleBuilder");

	return useMemo(() => {
		return (
			<IconButton
				primary
				labelPosition="left"
				icon="plus"
				onClick={onClick}
				disabled={disabled}
				className="conditionButton"
			>
				{dictionary.addCondition || "Add Condition"}
			</IconButton>
		);
	}, [ dictionary.addCondition, disabled, onClick ]);
};

export const getFormElementById = (formElements, elementId) => {
	return isNonEmptyArray(formElements)
		? formElements.find(formElement => formElement.id === elementId)
		: undefined;
};

export const getTruncatedLabel = (elementLabel, maxLength) => {
	return maxLength ? elementLabel.slice(0, maxLength) : elementLabel;
};

export const isAnswerAnyOption = optionId => {
	return !isNonEmptyString(optionId) || optionId === SELECT_ANY_KEY;
};

export const getTruncatedAndFullLabels = async ({
	formElements,
	formElementId,
	maxLength,
	optionId,
	dictionary,
}) => {
	const element = getFormElementById(formElements, formElementId);
	const elementLabel = element?.label || "";
	const truncatedElementLabel = getTruncatedLabel(elementLabel, maxLength);
	const isAny = isAnswerAnyOption(optionId);
	const ellipsis = truncatedElementLabel.length === maxLength ? "..." : "";
	let optionLabel;

	if (isAny) {
		optionLabel = dictionary.conditionIsAnsweredDisplay || "answered";
	} else {
		optionLabel = isFieldPicklist(element)
			? await lookupSingleEntryLabel(
				formElements,
				formElementId,
				optionId
			)
			: optionId;
	}

	const truncatedLabel = `${truncatedElementLabel}${ellipsis} ${
        dictionary.conditionSeparator || "is"
    } ${optionLabel}`;
	const fullLabel = `${elementLabel} ${
        dictionary.conditionSeparator || "is"
    } ${optionLabel}`;

	return {
		truncatedLabel,
		fullLabel,
		isTruncated: truncatedLabel !== fullLabel,
	};
};

export const lookupSingleEntryLabel = async (
	formElements,
	formElementId,
	optionId
) => {
	const formElement = formElements?.find(
		element => element.id === formElementId
	);

	if (!formElement) {
		return false;
	}

	const cayuseType = formElement.cayuseType;

	switch (cayuseType) {
	case CAYUSE_TYPES.ORG_FINDER: {
		const entry = await findOrganizationById(optionId);

		return entry?.name;
	}
	case CAYUSE_TYPES.UNIT_FINDER: {
		const entry = await findUnitById(optionId);

		return entry?.name;
	}
	case CAYUSE_TYPES.PERSON_FINDER: {
		const entry = await findPersonById(optionId);

		return entry?.fullName;
	}
	case CAYUSE_TYPES.RESEARCH_TEAM: {
		const entity = await api.researchRoles.findById(optionId);

		return entity?.name ?? "";
	}
	case CAYUSE_TYPES.FUNDING_PROGRAM: {
		const entity = await api.fundingPrograms.findById(optionId);

		return entity?.name
			? `${entity.organizationName} - ${entity.name}`
			: "";
	}
	default: {
		const entry = await findPicklistOptionByFieldIdAndOptionId(
			formElement,
			optionId
		);

		return entry?.label || optionId || "";
	}
	}
};

export const convertRules = rules => {
	(rules ?? []).forEach(rule => {
		if (!rule.condition) {
			return;
		}

		(rule.condition?.operands ?? []).forEach((group, groupIndex) => {
			groupIndex === 0 && (rule.condition.operators = []);

			group.operator && rule.condition.operators.push(group.operator);

			(group.operands ?? []).forEach((operand, index) => {
				index === 0 && (group.operators = []);
				operand.operator && group.operators.push(operand.operator);
			});
		});
	});

	return rules;
};

export const mapRule = rule => {
	(rule.condition.operands ?? []).forEach((group, groupIndex) => {
		if (groupIndex !== 0) {
			group.operator = rule.condition.operators
				? rule.condition.operators[groupIndex - 1]
				: null;
		}
		(group.operands ?? []).forEach((operand, index) => {
			if (index !== 0) {
				operand.operator = group.operators
					? group.operators[index - 1]
					: null;
			}
			operand.id = uuidv4();
		});

		return group;
	});

	rule.mapped = true;

	return rule;
};

export const getFormElementType = (formElementId, formElements) => {
	const formElement =
        formElements.find(element => element.id === formElementId) ?? {};

	return formElement.cayuseType ?? formElement.type;
};
